// Libraries
import momentTZ from "moment-timezone";

// MUI
import { GridColDef, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";

// Helpers
import { formatNumberWithCommas } from "@/Common/helpers/Modifiers/formatters";
import { errorLogger } from "@/Common/helpers/logger";

// Components
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { prepareGroupColDef } from "@/Layouts/components/TreeGrid/prepareGroupColDef";

// Constants
import { COLUMN_RESIZE_FACTOR, HEADER_ALIGN_RIGHT, HEADER_CLASS_NAME, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { tableKeys } from "@/Constants/tableKeys";
import { DATE_FORMAT } from "@/Constants/date";

const formatNumber = (value: number) => {
	const ZERO_WITH_TWO_DECIMALS = "0.00";
	try {
		if (!value) {
			return "";
		}

		return formatNumberWithCommas(value.toFixed(2).toString());
	} catch (error) {
		errorLogger(error as Error);
	}
	return ZERO_WITH_TWO_DECIMALS;
};

const COL_LONG = 105;
const COL_SHORT = 85;
const ONE_HUNDRED_EIGHTY = 210;

const createColumnsDef = (): GridColDef[] => [
	{
		field: "_id",
		headerName: "ID",
		width: 10,
		filterable: false,
	},
	{
		field: "fullName",
		headerName: "Associate Name",
		minWidth: ONE_HUNDRED_EIGHTY,
		width: ONE_HUNDRED_EIGHTY,
		maxWidth: ONE_HUNDRED_EIGHTY * COLUMN_RESIZE_FACTOR,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			return <CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? ONE_HUNDRED_EIGHTY} fontSize="12px" />;
		},
	},
	{
		field: "date",
		headerName: "Date",
		type: "date",
		width: COL_LONG,
		headerClassName: HEADER_CLASS_NAME,
		valueFormatter: (value) => momentTZ.utc(value).format(DATE_FORMAT.monthAbbrDatePadded),
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
	},
	{
		field: "clockInType",
		headerName: "Clock In Type",
		width: COL_LONG,
		headerClassName: HEADER_CLASS_NAME,
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
		renderHeader: (params) => <TypographyHeader params={params} />,
		align: "left",
	},
	{
		field: "clockInTime",
		headerName: "Clock In Time",
		type: "date",
		width: COL_LONG,
		valueFormatter: (value) => (value ? momentTZ.utc(value).format(DATE_FORMAT.time12h) : ""),
		align: "left",
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
		headerClassName: HEADER_CLASS_NAME,
	},
	{
		field: "clockOutType",
		headerName: "Clock Out Type",
		headerClassName: HEADER_CLASS_NAME,
		width: COL_LONG,
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
		align: "left",
	},
	{
		field: "clockOutTime",
		headerName: "Clock Out Time",
		align: "left",
		valueFormatter: (value) => (value ? momentTZ.utc(value).format(DATE_FORMAT.time12h) : ""),
		width: COL_LONG,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
		type: "date",
	},
	{
		field: "regular",
		type: "number",
		headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
		headerName: "Regular",
		valueFormatter: (value) => {
			return formatNumber(value as number);
		},
		renderHeader: (params) => <TypographyHeader params={params} />,
		align: "right",
		headerAlign: "right",
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
		width: COL_SHORT,
	},
	{
		field: "overtime",
		headerName: "OT",
		width: COL_SHORT,
		align: "right",
		headerAlign: "right",
		type: "number",
		renderHeader: (params) => <TypographyHeader params={params} />,
		valueFormatter: (value) => {
			return formatNumber(value as number);
		},
		headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
	},
	{
		field: "totalPaid",
		headerName: "Total Paid",
		headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
		align: "right",
		headerAlign: "right",
		type: "number",
		renderHeader: (params) => <TypographyHeader params={params} />,
		valueFormatter: (value) => {
			return formatNumber(value as number);
		},
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
		width: COL_SHORT,
	},
	{
		field: "unpaid",
		width: COL_SHORT,
		valueFormatter: (value) => {
			return formatNumber(value as number);
		},
		headerAlign: "right",
		align: "right",
		type: "number",
		renderCell: (params) => {
			return <TypographyCell params={params} issues={[]} hints={{ isFunded: false }} fontSize={"12px"} />;
		},
		headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
		headerName: "Unpaid",
		renderHeader: (params) => <TypographyHeader params={params} />,
	},
];

interface ITimesTableProps {
	rows: GridValidRowModel[];
	filterKeySuffix?: string;
}

const TimesTable = (props: ITimesTableProps) => {
	const { rows } = props;
	const apiRef = useGridApiRef();
	const columns = createColumnsDef();

	const groupingColDef = {
		...prepareGroupColDef(),
	};

	const getTreeDataPath = (row: GridValidRowModel) => {
		return row.hierarchy;
	};

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: `${tableKeys.DATA_PAGES.TIME_PAY_RATES_TIME}/${props.filterKeySuffix}`,
			}}
			apiRef={apiRef}
			treeData
			columns={columns}
			rows={rows}
			maxRows={18}
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			disableColumnMenu
			enableCustomScrollbars
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			disableChildrenFiltering
			columnVisibilityModel={{
				_id: false,
			}}
			slots={{
				noRowsOverlay: NoRowsOverlay,
				toolbar: CommonToolbar,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						className: "[&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
						hide: true,
					},
					filter: {
						hide: false,
					},
					search: {
						data: rows,
						hide: false,
						disabled: false,
						searchValue: "",
						parents: true,
						noEmptyParents: true,
					},
				},
				noRowsOverlay: {
					title: "No Work Hours or Pay Rates",
				},
			}}
			hideFooter
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"[&_.toolbar-actions]:!top-[0px]": true,
					"[&_.MuiDataGrid-main]:mt-[16px]": true,
				},
				expandableRows: false,
				hide: false,
			}}
		/>
	);
};

export default TimesTable;
